type EnvValue = string | boolean | number | undefined;

const truthyStrings = new Set(['1', 'true', 'yes', 'on']);

const toBoolean = (value: EnvValue): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    return truthyStrings.has(value.trim().toLowerCase());
  }
  return false;
};

const readEnv = (keys: string[]): EnvValue => {
  for (const key of keys) {
    if (typeof process !== 'undefined' && process.env && key in process.env) {
      return process.env[key];
    }
    if (typeof import.meta !== 'undefined' && (import.meta as any).env && key in (import.meta as any).env) {
      return (import.meta as any).env[key];
    }
  }
  return undefined;
};

export const isVerboseLoggingEnabled = (): boolean => {
  const raw = readEnv([
    'REACT_APP_VERBOSE_LOGS',
    'REACT_APP_VERBOSE_LOGGING',
    'VITE_VERBOSE_LOGS',
    'VITE_VERBOSE_LOGGING'
  ]);
  return toBoolean(raw);
};

export const isSearchDebugLoggingEnabled = (): boolean => {
  const raw = readEnv([
    'REACT_APP_SEARCH_DEBUG_LOGS',
    'VITE_SEARCH_DEBUG_LOGS',
    'SEARCH_DEBUG_LOGS'
  ]);
  if (typeof raw !== 'undefined') {
    return toBoolean(raw);
  }
  return false;
};

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent';

type LoggingConfig = {
  level: LogLevel;
  verboseChannels: Set<string>;
  warnChannels: Set<string>;
  mutedChannels: Set<string>;
  isProdLike: boolean;
};

const levelRank: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 50,
};

const normalizeChannel = (channel?: string | null): string => {
  if (!channel) return 'default';
  return channel
    .toString()
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]+/g, '')
    || 'default';
};

const parseLevel = (value: EnvValue): LogLevel | undefined => {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'debug') return 'debug';
  if (normalized === 'info') return 'info';
  if (normalized === 'warn' || normalized === 'warning') return 'warn';
  if (normalized === 'error') return 'error';
  if (normalized === 'silent' || normalized === 'off' || normalized === 'none') return 'silent';
  return undefined;
};

const parseChannelSet = (value: EnvValue): Set<string> => {
  if (typeof value !== 'string') return new Set();
  const parts = value
    .split(',')
    .map((part) => normalizeChannel(part))
    .filter(Boolean);
  return new Set(parts);
};

const resolveMode = (): string | undefined => {
  if (typeof import.meta !== 'undefined' && (import.meta as any).env?.MODE) {
    return (import.meta as any).env.MODE;
  }
  if (typeof process !== 'undefined' && process.env?.NODE_ENV) {
    return process.env.NODE_ENV;
  }
  return undefined;
};

const isProdLike = (): boolean => {
  const mode = resolveMode();
  if (!mode) return false;
  return mode.toLowerCase() === 'production';
};

const getDefaultLevel = (): LogLevel => 'warn';

const readChannels = (keys: string[]): Set<string> => {
  const raw = readEnv(keys);
  return parseChannelSet(raw);
};

let cachedConfig: LoggingConfig | null = null;

export const getLogConfig = (): LoggingConfig => {
  if (cachedConfig) return cachedConfig;

  const levelOverride = parseLevel(
    readEnv(['REACT_APP_LOG_LEVEL', 'VITE_LOG_LEVEL', 'LOG_LEVEL'])
  );
  const verboseChannels = readChannels([
    'REACT_APP_VERBOSE_CHANNELS',
    'REACT_APP_LOG_CHANNELS',
    'VITE_VERBOSE_CHANNELS',
    'VITE_LOG_CHANNELS',
    'LOG_CHANNELS',
  ]);
  const warnChannels = readChannels([
    'REACT_APP_WARN_CHANNELS',
    'VITE_WARN_CHANNELS',
    'LOG_WARN_CHANNELS',
  ]);
  const mutedChannels = readChannels([
    'REACT_APP_MUTED_CHANNELS',
    'VITE_MUTED_CHANNELS',
    'LOG_MUTED_CHANNELS',
  ]);

  cachedConfig = {
    level: levelOverride ?? getDefaultLevel(),
    verboseChannels,
    warnChannels,
    mutedChannels,
    isProdLike: isProdLike(),
  };

  return cachedConfig;
};

export const resolveBooleanEnv = (
  keys: string[],
  fallback?: boolean
): boolean | undefined => {
  const raw = readEnv(keys);
  if (typeof raw === 'undefined') return fallback;
  return toBoolean(raw);
};

const shouldLog = (level: LogLevel, channel: string, config: LoggingConfig): boolean => {
  const normalizedChannel = normalizeChannel(channel);
  const resolvedChannel = normalizedChannel || 'default';
  if (config.mutedChannels.has(resolvedChannel) || config.mutedChannels.has('*')) {
    return false;
  }

  const levelAllowed = levelRank[level] >= levelRank[config.level];
  if (!levelAllowed) {
    return false;
  }

  // For warn/error allow through unless warnChannels explicitly restricts.
  if (levelRank[level] >= levelRank.warn) {
    if (config.warnChannels.size > 0) {
      return (
        config.warnChannels.has(resolvedChannel) ||
        config.warnChannels.has('*')
      );
    }
    return true;
  }

  if (config.verboseChannels.size === 0) {
    // Without explicit channels, allow info/debug in non-prod by default.
    return !config.isProdLike;
  }

  return (
    config.verboseChannels.has(resolvedChannel) ||
    config.verboseChannels.has('*')
  );
};

type Logger = {
  debug: (...args: any[]) => void;
  info: (...args: any[]) => void;
  warn: (...args: any[]) => void;
  error: (...args: any[]) => void;
  log: (...args: any[]) => void;
};

const originalConsole: Console = { ...console };

export const createLogger = (channel: string): Logger => {
  const config = getLogConfig();
  const emit =
    (level: LogLevel, method: keyof Console) =>
    (...args: any[]) => {
      if (!shouldLog(level, channel, config)) return;
      const fn = (originalConsole[method] ?? console[method]) as
        | ((...fnArgs: any[]) => void)
        | undefined;
      if (typeof fn === 'function') {
        fn(...args);
      }
    };

  return {
    debug: emit('debug', 'debug'),
    info: emit('info', 'info'),
    log: emit('info', 'log'),
    warn: emit('warn', 'warn'),
    error: emit('error', 'error'),
  };
};

const deriveChannelFromArgs = (args: any[]): string => {
  const first = args?.[0];
  if (typeof first === 'string') {
    const match = first.match(/^\s*\[([^\]]+)\]/);
    if (match && match[1]) {
      return normalizeChannel(match[1]);
    }
  }
  return 'default';
};

let consoleGatesInstalled = false;

export const installConsoleLogGates = (): void => {
  if (consoleGatesInstalled || typeof console === 'undefined') return;
  const config = getLogConfig();

  const wrap =
    (level: LogLevel, method: keyof Console) =>
    (...args: any[]) => {
      const channel = deriveChannelFromArgs(args);
      if (!shouldLog(level, channel, config)) return;
      const fn = (originalConsole[method] ?? console[method]) as
        | ((...fnArgs: any[]) => void)
        | undefined;
      if (typeof fn === 'function') {
        fn(...args);
      }
    };

  console.debug = wrap('debug', 'debug');
  console.log = wrap('info', 'log');
  console.info = wrap('info', 'info');
  console.warn = wrap('warn', 'warn');
  console.error = wrap('error', 'error');
  if (typeof console.trace === 'function') {
    console.trace = wrap('debug', 'trace');
  }

  consoleGatesInstalled = true;
};
